import React, { useRef, useEffect, useState } from 'react';
import { 
  FaReact, 
  FaJs, 
  FaHtml5, 
  FaCss3Alt, 
  FaBootstrap, 
  FaWordpress, 
  FaDatabase,
  FaFigma 
} from 'react-icons/fa';
import { SiCanva } from 'react-icons/si';
import { SiTailwindcss } from 'react-icons/si';

const Skills = () => {
  const technicalSkills = [
    { name: 'React.js', icon: FaReact },
    { name: 'JavaScript', icon: FaJs },
    { name: 'Tailwind CSS', icon: SiTailwindcss },
    { name: 'HTML5', icon: FaHtml5 },
    { name: 'CSS3', icon: FaCss3Alt },
    { name: 'Bootstrap', icon: FaBootstrap },
    { name: 'WordPress', icon: FaWordpress },
    { name: 'MySQL', icon: FaDatabase },
    { name: 'Figma', icon: FaFigma },
    { name: 'Canva', icon: SiCanva }
  ];

  // Clone array multiple times to ensure enough track length for seamless infinite scrolling
  const marqueeItems = [...technicalSkills, ...technicalSkills, ...technicalSkills, ...technicalSkills];

  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let animationFrameId;
    let position = track.scrollLeft;
    let lastTime = performance.now();

    const scroll = (currentTime) => {
      // Use time delta for smooth framerate-independent scrolling
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      if (!isDragging) {
        // Scroll speed
        position += (0.05 * deltaTime); 
        
        // Seamless loop (when we reach exactly halfway through the duplicated content)
        if (position >= track.scrollWidth / 2) {
          position = 0;
        } else if (position < 0) {
          position = track.scrollWidth / 2;
        }
        
        track.scrollLeft = position;
      } else {
        // Sync position when dragging
        position = track.scrollLeft;
      }
      
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isDragging]);

  const handleDragStart = (x) => {
    setIsDragging(true);
    setStartX(x - trackRef.current.offsetLeft);
    setScrollLeft(trackRef.current.scrollLeft);
  };

  const handleDragMove = (x) => {
    if (!isDragging) return;
    const walk = (x - trackRef.current.offsetLeft - startX) * 1.5;
    trackRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <section className="pt-8 pb-16 sm:pt-12 sm:pb-24 bg-white dark:bg-black relative overflow-hidden transition-colors duration-300">
      {/* Draggable Marquee Track */}
      <div 
        ref={trackRef}
        className="flex gap-4 sm:gap-6 overflow-x-hidden px-4 cursor-grab active:cursor-grabbing select-none"
        onMouseDown={(e) => handleDragStart(e.pageX)}
        onMouseMove={(e) => handleDragMove(e.pageX)}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={(e) => handleDragStart(e.touches[0].pageX)}
        onTouchMove={(e) => handleDragMove(e.touches[0].pageX)}
        onTouchEnd={handleDragEnd}
      >
        {marqueeItems.map((skill, index) => {
          const IconComponent = skill.icon;
          return (
            <div 
              key={`${skill.name}-${index}`} 
              className="bg-white dark:bg-black border border-gray-200 dark:border-white/10 py-6 sm:py-8 px-6 sm:px-8 transition-all duration-300 hover:border-black dark:hover:border-white/50 flex flex-col items-center justify-center shrink-0 w-36 sm:w-48 group"
            >
              <div className="text-4xl sm:text-5xl mb-3 sm:mb-4 text-gray-400 dark:text-gray-600 group-hover:text-black dark:group-hover:text-white transition-colors duration-300 flex justify-center pointer-events-none">
                <IconComponent />
              </div>
              <div className="text-xs sm:text-sm font-semibold text-gray-500 dark:text-gray-500 group-hover:text-black dark:group-hover:text-white transition-colors duration-300 pointer-events-none">
                {skill.name}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Skills;